import { useEffect, useRef, useState, type ReactNode } from 'react';
import { UserAvatar } from '../components/UserAvatar';
import type { HeaderBrand, HeaderUserMenuItem } from './types';
import {
  useShellToolbarCenterContent,
  useShellToolbarLeadingActionsContent
} from './ShellToolbarContext';

const USER_MENU_ICONS: Record<string, string> = {
  profile: 'user',
  settings: 'preferences',
  preferences: 'preferences',
  'my-tickets': 'textdocument',
  feedback: 'comment',
  'report-bug': 'warning',
  logout: 'export'
};

function userDisplayName(username: string | null): string {
  if (!username?.trim()) return 'User';
  const local = username.split('@')[0]?.trim();
  return local || username;
}

function splitUserMenuItems(items: HeaderUserMenuItem[]) {
  const roleItem = items.find((item) => item.rolePill);
  const actionItems = items.filter((item) => !item.rolePill);
  return { roleItem, actionItems };
}

function renderUserMenuAction(item: HeaderUserMenuItem) {
  const icon = item.icon ?? USER_MENU_ICONS[item.id];
  return (
    <span className={`toolbar-user-menu-dropdown__item toolbar-user-menu-dropdown__item--${item.id}`}>
      {icon ? <i className={`dx-icon dx-icon-${icon} toolbar-user-menu-dropdown__icon`} aria-hidden /> : null}
      <span className="toolbar-user-menu-dropdown__label">{item.text}</span>
    </span>
  );
}

export interface PlatformHeaderProps {
  navOpen: boolean;
  onNavToggle: () => void;
  useCompactNav?: boolean;
  /** @deprecated Use useCompactNav. */
  isNarrow?: boolean;
  isTouchPrimary?: boolean;
  brand: HeaderBrand;
  username: string | null;
  themeMode: 'light' | 'dark';
  onThemeToggle: () => void;
  /** When false, hides toolbar light/dark control (Generic palettes without a light/dark pair). */
  showThemeToggle?: boolean;
  /** Product-specific controls rendered immediately before the theme toggle. */
  toolbarLeadingActions?: ReactNode;
  userMenuItems: HeaderUserMenuItem[];
  onUserMenuClick: (id: string) => void;
  roleBadge?: string | null;
  /** When known from profile, skips photo request when false. */
  hasProfilePhoto?: boolean | null;
  profilePhotoUrl?: string;
}

/**
 * Shell header — plain HTML (not dxToolbar/dxDropDownButton).
 * DevExtreme template render props recreate every React render and can hit
 * "Maximum update depth exceeded" (#185) under React 19.
 */
export function PlatformHeader({
  navOpen,
  onNavToggle,
  useCompactNav: useCompactNavProp,
  isNarrow,
  brand,
  username,
  themeMode,
  onThemeToggle,
  showThemeToggle = true,
  toolbarLeadingActions,
  userMenuItems,
  onUserMenuClick,
  roleBadge,
  hasProfilePhoto,
  profilePhotoUrl
}: PlatformHeaderProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const toolbarCenter = useShellToolbarCenterContent();
  const pageLeadingActions = useShellToolbarLeadingActionsContent();
  const { roleItem, actionItems } = splitUserMenuItems(userMenuItems);
  const useCompactNav = useCompactNavProp ?? isNarrow ?? false;

  useEffect(() => {
    if (!menuOpen) return;
    function onDocMouseDown(e: MouseEvent) {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setMenuOpen(false);
    }
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  function handleUserMenuAction(id: string) {
    setMenuOpen(false);
    onUserMenuClick(id);
  }

  const toolbarClass = [
    'app-toolbar',
    'app-toolbar--html',
    useCompactNav ? 'app-toolbar--compact' : '',
    toolbarCenter ? 'app-toolbar--has-center' : ''
  ]
    .filter(Boolean)
    .join(' ');
  const showSubtitle = Boolean(brand.subtitle) && !useCompactNav;
  const navActionLabel = navOpen ? 'Close navigation' : 'Open navigation';
  const menuTitle = username ? `${userDisplayName(username)} (${username})` : 'User menu';

  return (
    <header className={toolbarClass}>
      <div className="app-toolbar__before">
        <div className="toolbar-brand-block">
          <button
            type="button"
            className="toolbar-brand-logo-btn"
            onClick={onNavToggle}
            title={navActionLabel}
            aria-expanded={navOpen}
            aria-controls="app-sidebar"
            aria-label={navActionLabel}
          >
            <img src={brand.logoSrc} alt={brand.logoAlt ?? ''} className="toolbar-brand-logo" height={28} />
          </button>
          <div className="toolbar-brand-text">
            <h1>{brand.title}</h1>
            {showSubtitle ? <p>{brand.subtitle}</p> : null}
          </div>
        </div>
      </div>

      {toolbarCenter ? <div className="app-toolbar__center toolbar-center-content">{toolbarCenter}</div> : null}

      <div className="app-toolbar__after">
        <div className="toolbar-actions">
          {roleBadge && !useCompactNav ? (
            <span className="toolbar-role-badge" title="Highest assigned App Role">
              {roleBadge}
            </span>
          ) : null}
          {pageLeadingActions}
          {toolbarLeadingActions}
          {showThemeToggle ? (
            <button
              type="button"
              className="toolbar-icon-btn"
              title={themeMode === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
              aria-label={themeMode === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
              onClick={onThemeToggle}
            >
              <i className={`dx-icon dx-icon-${themeMode === 'dark' ? 'sun' : 'moon'}`} aria-hidden />
            </button>
          ) : null}
          <div className="toolbar-user-menu toolbar-user-menu--avatar-only" ref={menuRef}>
            <button
              type="button"
              className="toolbar-user-menu-trigger"
              title={menuTitle}
              aria-label={username ? `User menu for ${userDisplayName(username)}` : 'User menu'}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((open) => !open)}
            >
              <UserAvatar
                username={username}
                hasPhoto={hasProfilePhoto}
                photoUrl={profilePhotoUrl}
                size="sm"
                className="toolbar-user-menu-avatar"
              />
            </button>
            {menuOpen ? (
              <div className="toolbar-user-menu-panel toolbar-user-menu-panel--anchored" role="menu">
                {roleItem ? (
                  <div className="toolbar-user-menu-dropdown__header">
                    <span className="toolbar-user-menu-dropdown__role-pill">{roleItem.text}</span>
                  </div>
                ) : null}
                <div className="toolbar-user-menu-dropdown__actions">
                  {actionItems.map((item, index) => (
                    <button
                      key={item.id}
                      type="button"
                      role="menuitem"
                      className={[
                        'toolbar-user-menu-dropdown__action',
                        `toolbar-user-menu-dropdown__action--${item.id}`,
                        item.beginGroup && index > 0 ? 'toolbar-user-menu-dropdown__action--group-start' : ''
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      onClick={() => handleUserMenuAction(item.id)}
                    >
                      {renderUserMenuAction(item)}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </header>
  );
}
